import { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { FileText, Download, Receipt, Zap, CalendarRange, Sparkles, Eye, ExternalLink, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { downloadPDF as downloadPdfFile, previewPDF } from '../lib/pdf';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inrShort = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
function _two(n) {
  if (n < 20) return ONES[n];
  return TENS[Math.floor(n / 10)] + (n % 10 ? ' ' + ONES[n % 10] : '');
}
function _three(n) {
  let h = Math.floor(n / 100), r = n % 100;
  return (h ? ONES[h] + ' Hundred' + (r ? ' ' : '') : '') + (r ? _two(r) : '');
}
function inrInWords(num) {
  if (num == null) return '';
  num = Math.round(Number(num));
  if (num === 0) return 'Indian Rupees Zero Only';
  const crore = Math.floor(num / 10000000); num %= 10000000;
  const lakh = Math.floor(num / 100000); num %= 100000;
  const thousand = Math.floor(num / 1000); num %= 1000;
  const hundred = num;
  let str = '';
  if (crore) str += _two(crore) + ' Crore ';
  if (lakh) str += _two(lakh) + ' Lakh ';
  if (thousand) str += _two(thousand) + ' Thousand ';
  if (hundred) str += _three(hundred);
  return 'Indian Rupees ' + str.trim().replace(/\s+/g, ' ') + ' Only';
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
function fmtMonth(m) {
  // m = 'YYYY-MM' → 'May 2026'
  if (!m || typeof m !== 'string') return m || '';
  const [y, mm] = m.split('-');
  return `${MONTHS_LONG[parseInt(mm, 10) - 1] || mm} ${y}`;
}
function fmtMonthShort(m) {
  if (!m || typeof m !== 'string') return m || '';
  const [y, mm] = m.split('-');
  return `${MONTHS[parseInt(mm, 10) - 1] || mm} ${y}`;
}
function fmtDate(s) {
  if (!s) return '—';
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  } catch { return s; }
}
function payDate(month, total_days) {
  if (!month) return '';
  const [y, m] = month.split('-').map(Number);
  return fmtDate(`${y}-${String(m).padStart(2, '0')}-${String(total_days || 28).padStart(2, '0')}`);
}
function maskBankAcct(acc) {
  if (!acc) return '—';
  const s = String(acc);
  if (s.length <= 4) return s;
  return 'X'.repeat(s.length - 4) + s.slice(-4);
}

/* ----------- Payslip Body (matches the PDF) ----------- */
function PayslipBody({ data, logoFallback }) {
  if (!data) return null;
  const { payslip: p, company, branch, employee, leave_balance } = data;
  const earnings = p.earnings_lines || [
    { label: 'Basic Salary', amount: p.basic },
    { label: 'House Rent Allowance (HRA)', amount: p.hra },
    { label: 'Special Allowance', amount: p.allowances },
  ];
  const deductions = p.deductions_lines || [{ label: 'TDS / Income Tax', amount: p.tax_deduction || p.deductions }];
  const grossEarnings = p.gross || earnings.reduce((s, e) => s + Number(e.amount || 0), 0);
  const totalDeductions = (p.deductions != null ? p.deductions : deductions.reduce((s, e) => s + Number(e.amount || 0), 0));
  const netPay = p.net != null ? p.net : grossEarnings - totalDeductions;
  const logo = company?.logo_url || logoFallback;

  return (
    <div className="bg-white" data-testid="payslip-rendered" id="payslip-body">
      {/* HEADER */}
      <div className="flex items-start justify-between gap-6 pb-6 border-b border-slate-100">
        <div className="flex items-start gap-3 min-w-0">
          {logo && (
            <div
              className="w-12 h-12 flex-shrink-0 rounded-lg bg-center bg-no-repeat bg-contain"
              style={{ backgroundImage: `url(${logo})`, width: '48px', height: '48px' }}
              aria-label="Company logo"
            />
          )}
          <div className="min-w-0">
            <div className="font-heading text-lg font-bold text-slate-900 leading-tight">{company?.name || 'Lanvaus Private Limited'}</div>
            {branch ? (
              <div className="text-[11px] text-slate-600 leading-snug mt-1 space-y-0.5">
                <div className="font-medium text-slate-700">{branch.name}{branch.type === 'headquarters' ? ' (HQ)' : ''}</div>
                {branch.address && <div>{branch.address}</div>}
                {(branch.city || branch.state || branch.pincode) && (<div>{[branch.city, branch.state, branch.pincode].filter(Boolean).join(', ')}</div>)}
              </div>
            ) : (
              <div className="text-[11px] text-slate-600 leading-snug mt-1 space-y-0.5">
                {company?.address && <div>{company.address}</div>}
                {(company?.city || company?.state || company?.pincode) && (<div>{[company?.city, company?.state, company?.pincode].filter(Boolean).join(', ')}</div>)}
              </div>
            )}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="font-heading text-3xl font-bold text-slate-900 tracking-tight leading-none">PAYSLIP</div>
          <div className="text-sm text-slate-700 mt-2">{fmtMonth(p.month)}</div>
          <div className="text-xs text-slate-500 mt-0.5">Pay date: {payDate(p.month, p.total_days)}</div>
        </div>
      </div>
      <div className="h-[3px] bg-[#3D4A8E] -mt-px" />

      {/* EMPLOYEE DETAILS */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4 pt-6">
        <Field label="Employee Name" value={employee?.name} testId="ps-emp-name" />
        <Field label="Employee ID" value={employee?.employee_id} testId="ps-emp-id" />
        <Field label="Designation" value={employee?.title || (employee?.grade ? `Grade ${employee.grade}` : '—')} />
        <Field label="PAN" value={employee?.pan_number || '—'} />
        <Field label="Bank A/C" value={maskBankAcct(employee?.bank_account_number)} />
        <Field label="Paid Days" value={`${p.paid_days ?? '—'}/${p.total_days ?? '—'}`} testId="ps-paid-days" />
        <Field label="Date of Joining" value={fmtDate(employee?.joined_at)} />
        {employee?.department && <Field label="Department" value={employee.department} />}
        {employee?.bank_name && <Field label="Bank" value={employee.bank_name} />}
      </div>

      {/* LEAVE BALANCE */}
      {leave_balance && (
        <div className="mt-8">
          <div className="h-[2px] bg-[#3D4A8E]/30 mb-3" />
          <div className="flex items-center justify-between mb-2">
            <div className="font-heading font-bold text-sm uppercase tracking-wider text-slate-900">Leave Balance</div>
            <div className="font-heading font-bold text-sm uppercase tracking-wider text-slate-900">Days Available</div>
          </div>
          <div className="space-y-1.5 text-sm" data-testid="ps-leave-balance">
            <div className="flex justify-between"><span className="text-slate-700">Paid Leave</span><span className="font-medium text-slate-900">{leave_balance.remaining?.paid ?? 0}</span></div>
            <div className="flex justify-between"><span className="text-slate-700">Sick Leave</span><span className="font-medium text-slate-900">{leave_balance.remaining?.sick ?? 0}</span></div>
          </div>
        </div>
      )}

      {/* EARNINGS / DEDUCTIONS TABLE */}
      <div className="mt-8">
        <div className="h-[2px] bg-[#3D4A8E]/30" />
        <div className="grid grid-cols-2 gap-x-8 pt-3" data-testid="ps-earn-ded-table">
          {/* Column heads */}
          <div className="flex justify-between font-heading font-bold text-xs uppercase tracking-wider text-slate-900 pb-2 border-b border-slate-200">
            <span>Earnings</span><span>Amount (₹)</span>
          </div>
          <div className="flex justify-between font-heading font-bold text-xs uppercase tracking-wider text-slate-900 pb-2 border-b border-slate-200">
            <span>Deductions</span><span>Amount (₹)</span>
          </div>
          {/* Body rows */}
          <div className="space-y-2 pt-2">
            {earnings.map((e, i) => (
              <div key={`${e.label || 'earn'}-${i}`} className="flex justify-between text-sm py-1">
                <span className="text-slate-700">{e.label}</span>
                <span className="font-medium text-slate-900 tabular-nums">{Number(e.amount || 0).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="space-y-2 pt-2">
            {deductions.length > 0 ? deductions.map((d, i) => (
              <div key={`${d.label || 'ded'}-${i}`} className="flex justify-between text-sm py-1">
                <span className="text-slate-700">{d.label}</span>
                <span className="font-medium text-slate-900 tabular-nums">{Number(d.amount || 0).toFixed(2)}</span>
              </div>
            )) : (
              <div className="flex justify-between text-sm py-1 italic text-slate-500">
                <span>No deductions applicable</span><span>—</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* FINANCIAL SUMMARY */}
      <div className="mt-8">
        <div className="h-[2px] bg-[#3D4A8E]/30 mb-4" />
        <div className="grid grid-cols-2 gap-x-8 mb-4">
          <div className="flex justify-between text-sm">
            <span className="font-heading font-bold text-slate-900">Gross Earnings</span>
            <span className="font-heading font-bold text-slate-900 tabular-nums">{Number(grossEarnings).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="font-heading font-bold text-slate-900">Total Deductions</span>
            <span className="font-heading font-bold text-slate-900 tabular-nums">{Number(totalDeductions).toFixed(2)}</span>
          </div>
        </div>
        <div className="h-[3px] bg-[#3D4A8E] mb-4" />
        <div className="flex items-start justify-between gap-4" data-testid="ps-net-pay">
          <div>
            <div className="font-heading font-bold text-xl text-slate-900 tracking-wide leading-none">NET PAY</div>
            <div className="text-xs italic text-slate-500 mt-2 max-w-md">{inrInWords(netPay)}</div>
          </div>
          <span className="font-heading font-bold text-3xl text-[#3D4A8E] tabular-nums whitespace-nowrap">{inr(netPay)}</span>
        </div>
      </div>

      {/* FOOTER */}
      <div className="mt-10 pt-4 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
        <div>This is a computer-generated payslip and does not require a signature.</div>
        <div className="font-bold text-[#3D4A8E] tracking-wider">{(company?.name || 'LANVAUS').toUpperCase().split(' ')[0]} HR</div>
      </div>
    </div>
  );
}

const Field = ({ label, value, testId }) => (
  <div data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
    <div className="text-sm font-semibold text-slate-900 mt-0.5 break-words">{value || '—'}</div>
  </div>
);

/* ------ HTML for print/download (mirrors UI inside an isolated stylesheet) ------ */
function payslipHTML(data) {
  const { payslip: p, company, branch, employee, leave_balance } = data;
  const earnings = p.earnings_lines || [
    { label: 'Basic Salary', amount: p.basic },
    { label: 'House Rent Allowance (HRA)', amount: p.hra },
    { label: 'Special Allowance', amount: p.allowances },
  ];
  const deductions = p.deductions_lines || [{ label: 'TDS / Income Tax', amount: p.tax_deduction || p.deductions }];
  const grossEarnings = p.gross || earnings.reduce((s, e) => s + Number(e.amount || 0), 0);
  const totalDeductions = (p.deductions != null ? p.deductions : deductions.reduce((s, e) => s + Number(e.amount || 0), 0));
  const netPay = p.net != null ? p.net : grossEarnings - totalDeductions;
  const logo = company?.logo_url || `${window.location.origin}/lanvaus-logo.png`;
  const companyAddrLines = [company?.address, [company?.city, company?.state, company?.pincode].filter(Boolean).join(', ')].filter(Boolean);
  const fmtAmt = (n) => Number(n || 0).toFixed(2);
  return `<!doctype html><html><head><meta charset="utf-8"><title>Payslip ${fmtMonthShort(p.month)} - ${employee?.name || ''}</title>
<style>
  *{box-sizing:border-box} body{font-family:Manrope,'Helvetica Neue',Arial,sans-serif;background:#fff;color:#0F172A;margin:0;padding:24px}
  .wrap{max-width:780px;margin:auto;background:#fff;padding:32px 36px;border:1px solid #E2E8F0;border-radius:6px}
  .hdr{display:flex;justify-content:space-between;align-items:flex-start;gap:24px;padding-bottom:18px;border-bottom:1px solid #F1F5F9}
  .brand{display:flex;gap:12px;align-items:flex-start}
  .logo-box{width:48px;height:48px;flex-shrink:0;display:flex;align-items:flex-start;justify-content:center;margin-top:2px;overflow:hidden}
  .logo-box img{width:48px;height:48px;object-fit:contain;display:block}
  .cname{font-weight:700;font-size:16px;letter-spacing:-.01em;line-height:1.25}
  .caddr{font-size:11px;color:#475569;line-height:1.45;margin-top:4px}
  .caddr .bname{font-weight:600;color:#334155}
  .caddr > div + div{margin-top:1px}
  .pright{text-align:right}
  .ptitle{font-size:28px;font-weight:700;letter-spacing:-.01em;line-height:1}
  .pmonth{font-size:13px;color:#334155;margin-top:8px}
  .pdate{font-size:11px;color:#64748B}
  .accent{height:3px;background:#3D4A8E;margin-top:-1px}
  .grid3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:18px 24px;padding-top:22px}
  .grid2{display:grid;grid-template-columns:1fr 1fr;gap:0 32px}
  .lab{font-size:10px;text-transform:uppercase;letter-spacing:.08em;color:#64748B;font-weight:700}
  .val{font-size:13px;font-weight:600;color:#0F172A;margin-top:3px}
  .sect{margin-top:28px}
  .divider{height:2px;background:rgba(61,74,142,.3);margin-bottom:10px}
  .sect-head{display:flex;justify-content:space-between;font-weight:700;font-size:12px;text-transform:uppercase;letter-spacing:.08em;color:#0F172A;margin-bottom:8px}
  .row{display:flex;justify-content:space-between;font-size:13px;padding:4px 0}
  .row .l{color:#334155} .row .r{font-weight:500;font-variant-numeric:tabular-nums}
  .col-head{display:flex;justify-content:space-between;font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:.08em;color:#0F172A;padding-bottom:6px;border-bottom:1px solid #E2E8F0}
  .summary-grid{display:grid;grid-template-columns:1fr 1fr;gap:0 32px;font-size:13px;font-weight:700;margin-bottom:12px}
  .net-bar{height:3px;background:#3D4A8E;margin:14px 0}
  .net{display:flex;justify-content:space-between;align-items:baseline}
  .net .lbl{font-weight:700;font-size:18px;letter-spacing:.04em}
  .net .amt{font-weight:700;font-size:28px;color:#3D4A8E;font-variant-numeric:tabular-nums}
  .words{text-align:right;font-style:italic;font-size:11px;color:#64748B;margin-top:4px}
  .ftr{display:flex;justify-content:space-between;align-items:center;font-size:10px;color:#64748B;margin-top:32px;padding-top:14px;border-top:1px solid #F1F5F9}
  .ftr .hr{font-weight:700;color:#3D4A8E;letter-spacing:.1em}
  @media print { body{padding:0} .wrap{border:0;border-radius:0;box-shadow:none;max-width:none} }
</style></head>
<body>
<div class="wrap">
  <div class="hdr">
    <div class="brand">
      <img src="${logo}" alt="" />
      <div>
        <div class="cname">${company?.name || 'Lanvaus Private Limited'}</div>
        ${branch ? `<div class="caddr"><b>${branch.name}${branch.type === 'headquarters' ? ' (HQ)' : ''}</b>\n${branch.address || ''}${(branch.city || branch.state || branch.pincode) ? '\n' + [branch.city, branch.state, branch.pincode].filter(Boolean).join(', ') : ''}</div>` : `<div class="caddr">${companyAddrLines.join('\n')}</div>`}
      </div>
    </div>
    <div class="pright">
      <div class="ptitle">PAYSLIP</div>
      <div class="pmonth">${fmtMonth(p.month)}</div>
      <div class="pdate">Pay date: ${payDate(p.month, p.total_days)}</div>
    </div>
  </div>
  <div class="accent"></div>

  <div class="grid3">
    <div><div class="lab">Employee Name</div><div class="val">${employee?.name || '—'}</div></div>
    <div><div class="lab">Employee ID</div><div class="val">${employee?.employee_id || '—'}</div></div>
    <div><div class="lab">Designation</div><div class="val">${employee?.title || (employee?.grade ? 'Grade ' + employee.grade : '—')}</div></div>
    <div><div class="lab">PAN</div><div class="val">${employee?.pan_number || '—'}</div></div>
    <div><div class="lab">Bank A/C</div><div class="val">${maskBankAcct(employee?.bank_account_number)}</div></div>
    <div><div class="lab">Paid Days</div><div class="val">${p.paid_days ?? '—'}/${p.total_days ?? '—'}</div></div>
    <div><div class="lab">Date of Joining</div><div class="val">${fmtDate(employee?.joined_at)}</div></div>
    ${employee?.department ? `<div><div class="lab">Department</div><div class="val">${employee.department}</div></div>` : ''}
    ${employee?.bank_name ? `<div><div class="lab">Bank</div><div class="val">${employee.bank_name}</div></div>` : ''}
  </div>

  ${leave_balance ? `<div class="sect">
    <div class="divider"></div>
    <div class="sect-head"><span>Leave Balance</span><span>Days Available</span></div>
    <div class="row"><span class="l">Paid Leave</span><span class="r">${leave_balance.remaining?.paid ?? 0}</span></div>
    <div class="row"><span class="l">Sick Leave</span><span class="r">${leave_balance.remaining?.sick ?? 0}</span></div>
  </div>` : ''}

  <div class="sect">
    <div class="divider"></div>
    <div class="grid2">
      <div class="col-head"><span>Earnings</span><span>Amount (₹)</span></div>
      <div class="col-head"><span>Deductions</span><span>Amount (₹)</span></div>
      <div>
        ${earnings.map(e => `<div class="row"><span class="l">${e.label}</span><span class="r">${fmtAmt(e.amount)}</span></div>`).join('')}
      </div>
      <div>
        ${deductions.length ? deductions.map(d => `<div class="row"><span class="l">${d.label}</span><span class="r">${fmtAmt(d.amount)}</span></div>`).join('') : `<div class="row" style="font-style:italic;color:#64748B"><span class="l">No deductions applicable</span><span class="r">—</span></div>`}
      </div>
    </div>
  </div>

  <div class="sect">
    <div class="divider"></div>
    <div class="summary-grid">
      <div style="display:flex;justify-content:space-between"><span>Gross Earnings</span><span style="font-variant-numeric:tabular-nums">${fmtAmt(grossEarnings)}</span></div>
      <div style="display:flex;justify-content:space-between"><span>Total Deductions</span><span style="font-variant-numeric:tabular-nums">${fmtAmt(totalDeductions)}</span></div>
    </div>
    <div class="net-bar"></div>
    <div class="net"><span class="lbl">NET PAY</span><span class="amt">${inr(netPay)}</span></div>
    <div class="words">${inrInWords(netPay)}</div>
  </div>

  <div class="ftr">
    <span>This is a computer-generated payslip and does not require a signature.</span>
    <span class="hr">${(company?.name || 'LANVAUS').toUpperCase().split(' ')[0]} HR</span>
  </div>
</div>
</body></html>`;
}

function openPrint(data) {
  // Open as a Blob URL instead of document.write — same effect, safer (no doc-rewrite XSS surface).
  const html = payslipHTML(data);
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  const w = window.open(url, '_blank');
  if (!w) { URL.revokeObjectURL(url); toast.error('Pop-up blocked — allow pop-ups to view payslip'); return; }
  setTimeout(() => { try { w.focus(); w.print(); } catch { /* ignore */ } URL.revokeObjectURL(url); }, 800);
}

function PayslipViewer({ payslip, open, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  /* eslint-disable */
  useEffect(() => {
    let alive = true;
    if (!payslip?.id || !open) { setDetail(null); return; }
    setLoading(true);
    (async () => {
      try {
        const r = await api.get(`/payslips/${payslip.id}/detailed`);
        if (alive) setDetail(r.data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || 'Failed to load payslip');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [payslip?.id, open]);
  /* eslint-enable */

  if (!payslip) return null;

  const downloadPDF = async () => {
    if (!detail) return;
    setDownloading(true);
    const filename = `Payslip_${(detail.employee?.name || 'employee').replace(/\s+/g, '_')}_${fmtMonthShort(detail.payslip?.month)}.pdf`;
    await downloadPdfFile(`/payslips/${payslip.id}/pdf`, filename);
    setDownloading(false);
  };

  const preview = async () => {
    if (!detail) return;
    setDownloading(true);
    await previewPDF(`/payslips/${payslip.id}/pdf`);
    setDownloading(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="payslip-viewer">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Payslip · {fmtMonth(payslip.month)}</DialogTitle>
        </DialogHeader>
        {loading && <div className="text-slate-400 py-10 text-center">Loading payslip…</div>}
        {!loading && detail && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 mt-1 shadow-sm">
            <PayslipBody data={detail} logoFallback="/lanvaus-logo.png" />
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={preview} disabled={!detail || downloading} data-testid="payslip-preview" className="rounded-full">
            <ExternalLink className="w-4 h-4 mr-2" /> Open in new tab
          </Button>
          <Button onClick={downloadPDF} disabled={!detail || downloading} data-testid="payslip-download" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
            <Download className="w-4 h-4 mr-2" /> {downloading ? 'Working…' : 'Download PDF'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Payroll() {
  const { user } = useAuth();
  const canGenerate = (user?.roles || [user?.role]).some(r => ['admin', 'payroll_manager'].includes(r));
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [runMonth, setRunMonth] = useState(new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingToggle, setSavingToggle] = useState(false);

  const load = async () => {
    const [payslipRes, settingsRes] = await Promise.all([
      api.get('/payslips'),
      canGenerate ? api.get('/payroll/settings').catch(() => ({ data: null })) : Promise.resolve({ data: null }),
    ]);
    setList(payslipRes.data);
    if (settingsRes.data) setSettings(settingsRes.data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggleAuto = async (next) => {
    setSavingToggle(true);
    try {
      const { data } = await api.put('/payroll/settings', { auto_enabled: next });
      setSettings(data);
      toast.success(next ? 'Auto-payroll enabled — runs on 1st of every month' : 'Auto-payroll disabled — you will get reminders 5 days before month-end');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingToggle(false); }
  };

  const runPayroll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payroll/run-month?month=${runMonth}`);
      if (data.created === 0) toast.info(`No new payslips — already generated for ${runMonth}.`);
      else toast.success(`Generated ${data.created} payslip${data.created > 1 ? 's' : ''} for ${runMonth}.`);
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Compensation</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Payroll & Payslips</h1>
          <p className="text-slate-500 mt-1">Click any payslip to view it. Use <b>Open & print</b> to save as PDF via your browser.</p>
        </div>
        {canGenerate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="run-payroll-button" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0] text-white px-6 h-11">
                <Zap className="w-4 h-4 mr-2" /> Run payroll now
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="run-payroll-dialog" className="max-w-md">
              <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Sparkles className="w-5 h-5 text-[#3D4A8E]" /> Run monthly payroll</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <Label className="text-xs font-semibold text-slate-600">Month</Label>
                <Input data-testid="run-payroll-month" type="month" value={runMonth} onChange={(e) => setRunMonth(e.target.value)} className="rounded-xl" />
                <p className="text-xs text-slate-500 leading-relaxed">
                  Generates payslips for all <b>verified</b> employees with an annual CTC set. Includes manual deductions,
                  unpaid-leave pro-rating and credited reimbursements. Already-generated months are skipped.
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                <Button onClick={runPayroll} disabled={busy} data-testid="confirm-run-payroll" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
                  {busy ? 'Running…' : 'Generate & notify'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {canGenerate && settings && (
        <div data-testid="auto-payroll-card"
             className={`rounded-2xl p-6 flex items-start gap-4 border ${
               settings.auto_enabled
                 ? 'bg-gradient-to-br from-[#3D4A8E] to-[#283593] text-white border-transparent'
                 : 'bg-amber-50 text-amber-900 border-amber-200'
             }`}>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${settings.auto_enabled ? 'bg-white/15' : 'bg-amber-100'}`}>
            {settings.auto_enabled ? <CalendarRange className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="font-heading text-lg font-semibold">
                {settings.auto_enabled ? 'Auto-payroll is ON' : 'Auto-payroll is OFF — manual mode'}
              </div>
              <div className="flex items-center gap-2.5">
                <span className={`text-xs font-semibold ${settings.auto_enabled ? 'text-white/80' : 'text-amber-800'}`}>
                  {settings.auto_enabled ? 'On' : 'Off'}
                </span>
                <Switch
                  data-testid="auto-payroll-toggle"
                  checked={!!settings.auto_enabled}
                  disabled={savingToggle}
                  onCheckedChange={toggleAuto}
                  className="data-[state=checked]:bg-white/30 data-[state=unchecked]:bg-amber-300"
                />
              </div>
            </div>
            <p className={`text-sm mt-1 ${settings.auto_enabled ? 'text-white/80' : 'text-amber-900/90'}`}>
              {settings.auto_enabled
                ? <>Every 1st of the month at 02:00 UTC, payslips are auto-generated for all verified employees with a configured CTC. Toggle off to take manual control.</>
                : <>You must run payroll yourself before month-end. Starting <b>5 days before</b> each month-end, you and every Admin/HR will get a daily email reminder until payroll is run.</>}
            </p>
            {settings.current_month_run ? (
              <div className={`mt-3 text-xs inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${settings.auto_enabled ? 'bg-white/15 text-white' : 'bg-emerald-100 text-emerald-800'}`}
                   data-testid="current-month-status">
                <CheckCircle2 className="w-3.5 h-3.5" /> {settings.current_month} payroll already run · {settings.current_month_run_kind === 'auto' ? 'auto' : 'manual'}
              </div>
            ) : (
              <div className={`mt-3 text-xs inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${settings.auto_enabled ? 'bg-white/15 text-white' : 'bg-red-100 text-red-700'}`}
                   data-testid="current-month-status">
                <AlertTriangle className="w-3.5 h-3.5" /> {settings.current_month} payroll <b>not yet run</b>{!settings.auto_enabled && ' — run it before month-end'}
              </div>
            )}
            {settings.updated_by && (
              <div className={`mt-2 text-[11px] ${settings.auto_enabled ? 'text-white/60' : 'text-amber-700/80'}`}>
                Last changed by <b>{settings.updated_by}</b>{settings.updated_at ? ` · ${settings.updated_at.slice(0, 10)}` : ''}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {list.map(p => (
          <button key={p.id} onClick={() => setViewing(p)} data-testid={`payslip-${p.id}`}
                  className="text-left bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md hover:-translate-y-0.5 hover:border-[#3D4A8E]/40 transition-all">
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-xl bg-[#3D4A8E]/10 text-[#3D4A8E] flex items-center justify-center">
                <FileText className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div className="flex items-center gap-2">
                {p.auto_generated && (
                  <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-[#3D4A8E]/10 text-[#3D4A8E] flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Auto
                  </span>
                )}
                <div className="text-xs uppercase tracking-wider text-slate-400 font-bold">{fmtMonthShort(p.month)}</div>
              </div>
            </div>
            <div className="mt-4">
              <div className="font-heading font-semibold text-slate-900">{p.employee_name}</div>
              <div className="text-xs text-slate-500">{p.employee_email}</div>
              {p.employee_code && <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mt-1">{p.employee_code}</div>}
            </div>
            <div className="mt-3 text-xs text-slate-500 space-y-0.5">
              <div className="flex justify-between"><span>Gross</span><span>{inrShort(p.gross)}</span></div>
              <div className="flex justify-between"><span>Deductions</span><span className="text-red-500">-{inrShort(p.deductions)}</span></div>
            </div>
            <div className="mt-3 flex items-end justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500">Net pay</div>
                <div className="font-heading text-2xl font-bold text-slate-900">{inrShort(p.net)}</div>
              </div>
              <div className="text-xs text-[#3D4A8E] flex items-center gap-1 font-medium">
                <Eye className="w-3.5 h-3.5" /> View
              </div>
            </div>
          </button>
        ))}
        {list.length === 0 && (
          <div className="col-span-full text-center py-16 text-slate-400 flex flex-col items-center gap-3">
            <Receipt className="w-10 h-10 text-slate-300" />
            <div>No payslips yet. {canGenerate && 'Click "Run payroll now" to generate.'}</div>
          </div>
        )}
      </div>

      <PayslipViewer payslip={viewing} open={!!viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
